import { useRef, useState, useEffect, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import {
  Button as PennyButton,
  Checkbox,
  Container,
  Form,
  FormField,
  Group,
  PhoneField,
  SelectNew,
  Text,
  TextArea,
  TextField,
  useMelioForm,
} from '@melio/penny';

import { useApp, Button, Banner, Icon, IconButton, Illustration, MelioLogo } from '@/components/ds';
import { IndustryField, AddressFields } from '@/components/form';
import { onboardOrExistingPartner, ensureEntityForEnv } from '@/api/portal';
import { trackEvent, useFlowAnalytics } from '@/lib/analytics';
import { GATE_ENV, ONBOARDING_ENVS } from '@/config/environments';
import { SCHEDULE_URL } from '@/config/links';
import type { Go } from '@/lib/types';

// onboarding.tsx — post-login onboarding for a signed-in (auth-service) user who has no partner yet.
// Flow (each a top-level milestone, like the welcome flow): usecase (beta application) → you (identity)
// → business (profile) → legal (business type & tax) → address (legal + operating, one milestone with
// an inner Legal/Operating sub-step) → done. On submit the whole form is wrapped into a beta application
// and sent for vetting (auto-approved today); once approved the backend creates/reuses the partner and
// issues a key in every provisioning env (staging01 + production), and the KYB business entity is then
// created client-side per env (it needs a browser Sonar token). The PennyProvider/IntlProvider wrapping
// is applied app-wide in lib/penny.

const emptyAddress = { _search: undefined, line1: '', line2: '', city: '', state: '', postalCode: '', aptNumber: '' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'usecase' | 'you' | 'owner' | 'business' | 'legal' | 'address' | 'review' | 'done';

// "Which describes your integration?" — mirrors the beta application form's audience question.
const buildingForOptions = [
  { value: 'own', label: 'Direct', description: "We're paying our own vendors and contractors." },
  {
    value: 'platform',
    label: 'Platform',
    description: "We're building a platform so other businesses can pay their vendors and contractors.",
  },
];

interface FieldSpec {
  name: string;
  label: string;
  test?: (v: any) => boolean;
  message?: string;
}

const businessTypeOptions = [
  {
    value: 'sole-proprietorship',
    label: 'Sole Proprietorship',
    description: 'An unincorporated one-person business (e.g., freelancer, independent contractor).',
  },
  { value: 'partnership', label: 'Partnership', description: 'A business owned by two or more people (e.g., General, LP, LLP).' },
  {
    value: 'llc',
    label: 'Limited Liability Company (LLC)',
    description: 'A registered business structure that protects owners from personal liability.',
  },
  { value: 'corporation', label: 'Corporation', description: 'A registered legal entity (e.g., C-Corp, S-Corp, Inc.).' },
  { value: 'trust', label: 'Trust', description: 'A fiduciary arrangement managed by a trustee on behalf of beneficiaries.' },
  {
    value: 'non-profit',
    label: 'Non-Profit',
    description: 'A tax-exempt organization operating for charitable, educational, or public purposes.',
  },
  { value: 'municipality', label: 'Municipality', description: 'A local government entity such as a city, town, or county.' },
  { value: 'ngo', label: 'NGO', description: 'A non-governmental organization operating independently of government.' },
];

const defaultTaxTypeForBusinessType = (type: string) => (type === 'sole-proprietorship' ? 'ssn' : 'ein');

const taxTypeOptions = [
  { value: 'ein', label: 'EIN' },
  { value: 'ssn', label: 'SSN' },
  { value: 'itin', label: 'ITIN' },
];

// Validation spec lists — defined at module level (no closure deps) so refs stay stable.
const useCaseSpecs: FieldSpec[] = [
  { name: 'buildingFor', label: 'Which describes your integration', message: 'Select which describes your integration' },
  { name: 'projectDescription', label: 'What you’re building' },
];
// "Tell us about you" = the point of contact (the signed-in person filling this out).
const aboutYouSpecs: FieldSpec[] = [
  { name: 'contactFirst', label: 'First name' },
  { name: 'contactLast', label: 'Last name' },
  { name: 'contactDob', label: 'Date of birth', test: (v) => v instanceof Date && !isNaN(v as unknown as number) },
  { name: 'contactPhone', label: 'Personal phone number' },
];
// Business owner — always validated: the owner step has no "same as point of contact" skip (only the
// address step skips, via operatingSameAsLegal). Prefilled from the point of contact + the signed-in
// email in goToOwner, then editable. `ownerEmail` is the address the beta application records, so the
// EMAIL_RE gate below is what guarantees `/onboard` never receives an application without a valid one.
const ownerSpecs: FieldSpec[] = [
  { name: 'ownerFirst', label: 'First name' },
  { name: 'ownerLast', label: 'Last name' },
  { name: 'ownerEmail', label: 'Owner email', test: (v) => EMAIL_RE.test(String(v ?? '').trim()), message: 'Enter a valid email' },
  { name: 'ownerDob', label: 'Date of birth', test: (v) => v instanceof Date && !isNaN(v as unknown as number) },
  { name: 'ownerPhone', label: 'Owner phone number' },
];
const businessInfoSpecs: FieldSpec[] = [
  { name: 'legalName', label: 'Business name' },
  { name: 'industry', label: 'Industry', test: (v) => !!(v && v.naicsCode), message: 'Select an industry' },
  { name: 'phone', label: 'Business phone' },
  // Required for onboarding: the website becomes the partner's product URL in the email-service config.
  { name: 'website', label: 'Business website' },
];
const legalInfoSpecs: FieldSpec[] = [{ name: 'taxId', label: 'Tax ID' }];
const legalAddressSpecs: FieldSpec[] = [
  { name: 'legalAddress.line1', label: 'Address line 1' },
  { name: 'legalAddress.city', label: 'City' },
  { name: 'legalAddress.state', label: 'State' },
  { name: 'legalAddress.postalCode', label: 'Postal code' },
];
const addressSpecs: FieldSpec[] = [
  { name: 'address.line1', label: 'Address line 1' },
  { name: 'address.city', label: 'City' },
  { name: 'address.state', label: 'State' },
  { name: 'address.postalCode', label: 'Postal code' },
];

// The title + intro copy every wizard step opens with. `as="h2"` keeps the heading role the
// e2e suite (and any screen-reader user) navigates by; penny's type scale supplies the rest.
function StepHeader({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <Container paddingBottom="l">
      <Text as="h2" textStyle="heading2Semi">
        {title}
      </Text>
      {children && (
        <Container paddingTop="xs">
          <Text textStyle="body2" color="semantic.text.secondary">
            {children}
          </Text>
        </Container>
      )}
    </Container>
  );
}

// Normalize a phone to a leading-+ form when we can (US 10/11 digit); otherwise leave as typed.
function normPhone(v: any): string {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('+')) return raw.replace(/[^\d+]/g, '');
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return '+1' + d;
  if (d.length === 11 && d.startsWith('1')) return '+' + d;
  return raw;
}

// The legal notice lives at the foot of the form panel (a light surface), so these read as
// ordinary body links rather than the inverse treatment the dark brand panel needed.
const LEGAL_LINK: CSSProperties = { color: 'var(--text-primary)', textDecoration: 'underline' };

// Shown under the form on every step: the same notice the welcome page gives before it takes any
// business details. It sits with the form rather than on the brand panel because it qualifies the
// Continue button the user is about to press.
function LegalNotice() {
  return (
    <Text as="p" textStyle="caption1" color="semantic.text.secondary">
      By continuing, you accept Melio&apos;s{' '}
      <a href="https://meliopayments.com/privacy-policy/" target="_blank" rel="noreferrer" style={LEGAL_LINK}>
        Privacy Policy
      </a>{' '}
      and{' '}
      <a href="https://developers.melio.com/api-agreement" target="_blank" rel="noreferrer" style={LEGAL_LINK}>
        API Agreement
      </a>
      . Payment services are subject to Melio&apos;s{' '}
      <a href="https://meliopayments.com/terms-of-service/" target="_blank" rel="noreferrer" style={LEGAL_LINK}>
        Terms of Service
      </a>
      .
    </Text>
  );
}

// One label/value row in the review summary (label left, value right, bottom rule).
function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Container paddingY="xs" style={{ borderBottom: '1px solid var(--border-static)' }}>
      <Group variant="horizontal" justifyContent="space-between" spacing="m" alignItems="baseline">
        <Text textStyle="body4" color="semantic.text.secondary">
          {label}
        </Text>
        <Text textStyle="body4Semi" style={{ textAlign: 'right' }}>
          {value || '—'}
        </Text>
      </Group>
    </Container>
  );
}

interface KeyRevealProps {
  apiKey: string;
  onContinue?: () => void;
  continueLabel?: string;
}

// The key itself plus the copy/mask controls and the "treat this like a password" warning —
// everything that is true wherever a freshly-minted key is shown. The surrounding chrome differs
// by context, so it lives with the caller: the onboarding step wraps this in a badge + heading +
// a full-width continue button, while the dashboard shows it in a drawer that already supplies
// both a header and a footer.
function ApiKeyPanel({ apiKey }: { apiKey: string }) {
  const [masked, setMasked] = useState(false);
  const [copied, setCopied] = useState(false);
  const display = masked ? apiKey.slice(0, 8) + '\u2022'.repeat(apiKey.length - 8) : apiKey;
  const copy = () => {
    try {
      navigator.clipboard.writeText(apiKey);
    } catch (e) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <>
      <Container paddingBottom="m">
        <Text textStyle="body2" color="semantic.text.secondary">
          This is the only time we&apos;ll show it in full. Copy it now and store it somewhere safe — like a secrets manager.
        </Text>
      </Container>
      {/* Deliberately a dark "secret" surface, so the colors here are literal rather than tokens —
          penny has no dark-surface container variant. The controls on it use penny's inverse variants. */}
      <div
        style={{
          background: '#16171f',
          borderRadius: 12,
          border: '1px solid #2c2f3d',
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Icon name="lock" size={16} color="#5c6472" />
        <code className="om-mono" style={{ flex: 1, fontSize: 14, color: '#8fd6a8', letterSpacing: '.01em', wordBreak: 'break-all' }}>
          {display}
        </code>
        <IconButton
          name={masked ? 'show' : 'hide'}
          ariaLabel={masked ? 'Show the full key' : 'Hide the key'}
          variant="naked-inverse"
          size={32}
          onClick={() => setMasked((m) => !m)}
        />
        <PennyButton
          size="small"
          variant={copied ? 'success' : 'primary'}
          label={copied ? 'Copied' : 'Copy'}
          leftElement={<Icon name={copied ? 'checked' : 'copy'} size={15} />}
          onClick={copy}
        />
      </div>
      <Banner kind="warning" style={{ marginTop: 16 }}>
        Treat this like a password. Anyone with this key can move money on your account. Never expose it in a browser or commit it to git.
      </Banner>
    </>
  );
}

// The onboarding step's presentation of a new key: the lock badge and heading that give it weight
// as a milestone in the wizard, and a full-width button on to the next thing.
function KeyReveal({ apiKey, onContinue, continueLabel = 'Continue to dashboard' }: KeyRevealProps) {
  return (
    <div style={{ animation: 'om-fade-up .3s ease' }}>
      <Container width="fit-content" paddingX="s" paddingY="s" backgroundColor="light" style={{ borderRadius: 14, marginBottom: 18 }}>
        <Icon name="lock" size={26} color="var(--accent)" />
      </Container>
      <Container paddingBottom="xs">
        <Text as="h2" textStyle="heading2Semi">
          Your secret API key
        </Text>
      </Container>
      <ApiKeyPanel apiKey={apiKey} />
      <Button fullWidth size="large" style={{ marginTop: 22 }} rightIcon="arrow-right" onClick={onContinue}>
        {continueLabel}
      </Button>
    </div>
  );
}

interface OnboardingProps {
  go: Go;
}

function Onboarding({ go }: OnboardingProps) {
  const app = useApp();
  // Boots FullStory + Segment for this flow and tags the session (see lib/analytics.ts). The wizard
  // collects tax ids, owner emails, names and addresses — none of that may ever be sent as an event
  // property; only safe enums (use case, business type, tax id type) and counts are tracked.
  useFlowAnalytics('onboarding');

  // The address step has an inner sub-step: 0 = legal registration, 1 = operating (mirrors the welcome flow).
  const [step, setStep] = useState<Step>('usecase');
  const [addressSub, setAddressSub] = useState(0);

  // Funnel entry: fire once when a not-yet-onboarded user actually starts the wizard (skipped for the
  // already-onboarded user who gets bounced to /dashboard below).
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current || app.onboarded === true) return;
    startedRef.current = true;
    trackEvent('onboarding', 'Onboarding Started', {});
  }, [app.onboarded]);

  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  // What provisioning is currently doing. Surfaced under the submit button because the work is
  // several network round-trips per environment and can run for a while — long enough that a bare
  // spinner reads as stalled.
  const [submitPhase, setSubmitPhase] = useState('');

  // Already-onboarded users don't belong in the wizard — bounce once on mount. (The route guard only
  // checks auth, so that marking the user onboarded at the end doesn't yank them off the key reveal.)
  const bouncedRef = useRef(false);
  useEffect(() => {
    if (!bouncedRef.current && app.onboarded === true) {
      bouncedRef.current = true;
      go('/dashboard');
    }
  }, [app.onboarded, go]);

  // Refs for re-validation subscription (avoid stale closures)
  const stepAttemptedRef = useRef(false);
  const currentSpecsRef = useRef<FieldSpec[]>([]);

  const goStep = (s: Step) => {
    trackEvent('onboarding', 'Step Viewed', { step: s, subStep: 0 });
    setStep(s);
    setAddressSub(0);
    setSubmitErr('');
    stepAttemptedRef.current = false;
  };
  const goAddressSub = (n: number) => {
    trackEvent('onboarding', 'Step Viewed', { step: 'address', subStep: n });
    setAddressSub(n);
    setSubmitErr('');
    stepAttemptedRef.current = false;
  };

  const form: any = useMelioForm({
    defaultValues: {
      buildingFor: '',
      projectDescription: '',
      // Point of contact (the signed-in person)
      contactFirst: '',
      contactLast: '',
      contactPhone: '',
      contactDob: null,
      // Business owner — prefilled from the point of contact (+ logged-in email), then editable
      ownerFirst: '',
      ownerLast: '',
      ownerEmail: '',
      ownerPhone: '',
      ownerDob: null,
      legalName: '',
      industry: undefined,
      phone: '',
      website: '',
      businessType: 'llc',
      taxIdType: 'ein',
      taxId: '',
      description: '',
      address: { ...emptyAddress },
      legalAddress: { ...emptyAddress },
      operatingSameAsLegal: true,
    },
    onSubmit: () => {
      if (step === 'address' && addressSub === 1) goToReview();
      else if (step === 'review') createPartner();
    },
  });
  const { formProps, registerField, watch, getValues, setValue, setError, clearErrors } = form;

  const taxIdType = watch('taxIdType');
  const businessType = watch('businessType');
  const operatingSameAsLegal = watch('operatingSameAsLegal');

  useEffect(() => {
    setValue('taxIdType', defaultTaxTypeForBusinessType(businessType));
  }, [businessType]);

  // Keep currentSpecsRef in sync with the active step/substep so the watch
  // subscription below (set up once) can re-validate without stale closures.
  useEffect(() => {
    if (step === 'usecase') currentSpecsRef.current = useCaseSpecs;
    else if (step === 'you') currentSpecsRef.current = aboutYouSpecs;
    else if (step === 'owner') currentSpecsRef.current = ownerSpecs;
    else if (step === 'business') currentSpecsRef.current = businessInfoSpecs;
    else if (step === 'legal') currentSpecsRef.current = legalInfoSpecs;
    else if (step === 'address') currentSpecsRef.current = addressSub === 0 ? legalAddressSpecs : operatingSameAsLegal ? [] : addressSpecs;
    else currentSpecsRef.current = [];
  }, [step, addressSub, operatingSameAsLegal]);

  // Validate on change after the step has been attempted (Continue clicked).
  // This is the "re-validate on change after field is visited" behavior.
  useEffect(() => {
    const sub = watch(() => {
      if (!stepAttemptedRef.current) return;
      const specs = currentSpecsRef.current;
      if (!specs.length) return;
      // Clear only current step's fields then re-evaluate
      specs.forEach((s) => clearErrors(s.name));
      const v = getValues();
      const get = (path: string) => path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), v);
      let firstMsg = '';
      for (const s of specs) {
        const val = get(s.name);
        const ok = s.test ? s.test(val) : !(val == null || val === '');
        if (!ok) {
          setError(s.name, { type: 'required', message: s.message || 'Required' });
          if (!firstMsg) firstMsg = `Please complete: ${s.label}`;
        }
      }
      setSubmitErr(firstMsg);
    });
    return () => sub?.unsubscribe?.();
  }, []);

  // Validate a set of specs, set field-level errors, return first message.
  const checkRequired = (specs: FieldSpec[]) => {
    specs.forEach((s) => clearErrors(s.name));
    const v = getValues();
    const get = (path: string) => path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), v);
    let firstMsg = '';
    for (const s of specs) {
      const val = get(s.name);
      const ok = s.test ? s.test(val) : !(val == null || val === '');
      if (!ok) {
        setError(s.name, { type: 'required', message: s.message || 'Required' });
        if (!firstMsg) firstMsg = `Please complete: ${s.label}`;
      }
    }
    return firstMsg;
  };

  // Advance helper: validate specs and proceed; marks step as attempted for re-validation.
  const advance = (specs: FieldSpec[] | (() => FieldSpec[]), callback: () => void) => {
    stepAttemptedRef.current = true;
    const err = checkRequired(typeof specs === 'function' ? specs() : specs);
    if (err) {
      setSubmitErr(err);
      return;
    }
    setSubmitErr('');
    stepAttemptedRef.current = false;
    callback();
  };

  const goToYou = () =>
    advance(useCaseSpecs, () => {
      trackEvent('onboarding', 'Step Completed', { step: 'usecase', buildingFor: getValues().buildingFor });
      goStep('you');
    });
  // Prefill the owner once from the point of contact (+ the signed-in email); the fields stay editable
  // for the case where the owner is someone other than the person filling this out.
  const ownerPrefilledRef = useRef(false);
  const goToOwner = () =>
    advance(aboutYouSpecs, () => {
      if (!ownerPrefilledRef.current) {
        ownerPrefilledRef.current = true;
        const v = getValues();
        setValue('ownerFirst', v.contactFirst);
        setValue('ownerLast', v.contactLast);
        setValue('ownerEmail', app.email ?? '');
        setValue('ownerDob', v.contactDob);
        setValue('ownerPhone', v.contactPhone);
      }
      trackEvent('onboarding', 'Step Completed', { step: 'you' });
      goStep('owner');
    });
  const goToBusiness = () =>
    advance(ownerSpecs, () => {
      trackEvent('onboarding', 'Step Completed', { step: 'owner' });
      goStep('business');
    });

  const goToLegal = () =>
    advance(businessInfoSpecs, () => {
      trackEvent('onboarding', 'Step Completed', {
        step: 'business',
        industryCode: getValues().industry?.naicsCode,
        hasWebsite: !!getValues().website,
      });
      goStep('legal');
    });
  const goToAddress = () =>
    advance(legalInfoSpecs, () => {
      trackEvent('onboarding', 'Step Completed', {
        step: 'legal',
        businessType: getValues().businessType,
        taxIdType: getValues().taxIdType,
      });
      goStep('address');
    });
  const goToOperating = () =>
    advance(legalAddressSpecs, () => {
      trackEvent('onboarding', 'Step Completed', { step: 'address', subStep: 0 });
      goAddressSub(1);
    });
  // From the operating sub-step, validate the address(es) then move to the review summary.
  const goToReview = () =>
    advance(operatingSameAsLegal ? legalAddressSpecs : [...legalAddressSpecs, ...addressSpecs], () => {
      trackEvent('onboarding', 'Step Completed', { step: 'address', subStep: 1, operatingSameAsLegal: getValues().operatingSameAsLegal });
      goStep('review');
    });

  const mkAddress = (a: any) => ({
    line1: a.line1,
    ...(a.line2 ? { line2: a.line2 } : {}),
    city: a.city,
    state: a.state,
    postalCode: a.postalCode,
    ...(a.aptNumber ? { aptNumber: a.aptNumber } : {}),
    countryCode: 'US',
  });

  // Final submit: provision the partner + keys (backend), then create the KYB business entity in each
  // env with the issued key (client-side, for the Sonar token). Idempotent/resumable server-side.
  // Re-entrancy guard. Penny's loading button sets `pointer-events: none`, which stops the mouse
  // but not the keyboard — the button stays focusable, and this step also submits on Enter (see
  // the form's onSubmit). Provisioning is several non-trivial calls per environment, so a second
  // concurrent run is worth refusing outright. A ref rather than the `submitting` state because
  // this closure would otherwise read a stale value.
  const submittingRef = useRef(false);
  const createPartner = async () => {
    if (submittingRef.current) return;
    stepAttemptedRef.current = true;
    const extraSpecs = operatingSameAsLegal ? legalAddressSpecs : [...legalAddressSpecs, ...addressSpecs];
    const err = checkRequired(extraSpecs);
    if (err) {
      setSubmitErr(err);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitErr('');
    // Two round-trips per environment (provision the partner, then create the KYB entity), so the
    // phase counter is over that total rather than over the environment list.
    const totalPhases = ONBOARDING_ENVS.length * 2;
    let phase = 0;
    const reportPhase = (label: string) => setSubmitPhase(`${label} (${++phase} of ${totalPhases})`);
    try {
      const v = getValues();
      const email = app.email ?? '';
      const legalAddr = mkAddress(v.legalAddress);
      const operatingAddr = v.operatingSameAsLegal ? legalAddr : mkAddress(v.address);
      const entityBody: Record<string, unknown> = {
        type: 'business',
        externalId: 'partner-biz-' + crypto.randomUUID().slice(0, 8),
        name: v.legalName,
        legalName: v.legalName,
        phoneNumber: v.phone,
        businessType: v.businessType,
        taxInfo: { type: v.taxIdType, identifier: v.taxId },
        industry: { naicsCode: v.industry?.naicsCode, ...(v.industry?.name ? { name: v.industry.name } : {}) },
        contact: { firstName: v.contactFirst, lastName: v.contactLast },
        ...(v.website ? { website: v.website } : {}),
        address: operatingAddr,
        legalAddress: legalAddr,
        // The business owner. Prefilled from the point of contact (+ signed-in email) but editable,
        // so these fields are the source of truth even when the owner is the person filling this out.
        owner: {
          firstName: v.ownerFirst,
          lastName: v.ownerLast,
          email: v.ownerEmail,
          ...(v.ownerDob instanceof Date ? { dateOfBirth: v.ownerDob.toISOString().split('T')[0] } : {}),
          ...(v.ownerPhone ? { phoneNumber: v.ownerPhone } : {}),
        },
      };

      // The beta application (use case + business details) is sent to `/onboard`, which vets it server-side
      // and holds the flow on a decline before the partner is created — auto-approved for now, so there's
      // no client-side gate here. `email` is the business owner's, collected on the owner step: the access
      // token carries no email claim (auth-service puts it only in the id_token), so the server can't fill one.
      const application = {
        email: v.ownerEmail,
        companyName: v.legalName,
        website: v.website || undefined,
        companyAddress: [legalAddr.line1, legalAddr.city, legalAddr.state, legalAddr.postalCode].filter(Boolean).join(', ') || undefined,
        industry: v.industry?.name || v.industry?.naicsCode || undefined,
        buildingFor: v.buildingFor,
        projectDescription: v.projectDescription,
      };

      // Provision the partner in each env by calling that env's portal API directly — one call per env,
      // no server-side cross-env fan-out — then create the KYB entity in that env with the user's access
      // token (never the api-key; Sonar is minted from the non-secret partnerName inside the helper).
      // TEMPORARY (staging01 testing): production is best-effort. A non-production build can't actually
      // reach production's portal API — `portalApiBase('production')` is the same-origin `/api`, which on
      // developers.staging01.melio.com CloudFront routes back to *staging01*'s portal API (hence the second
      // `/onboard`, answered 409 PARTNER_ALREADY_EXISTS), while the KYB entity create does go to the real
      // api.melio.com and rejects the staging01 token. Skip a production failure and finish with the envs
      // that did provision, instead of failing the whole wizard. Remove once the prod leg is addressed properly.
      let primaryPartnerName: string | undefined;
      const skippedEnvs: string[] = [];
      for (const env of ONBOARDING_ENVS) {
        const token = await app.getAccessToken();
        if (!token) throw new Error('Your session has expired. Please sign in again.');

        try {
          reportPhase('Creating your partner account');
          const partnerName = await onboardOrExistingPartner(env, token, application);
          reportPhase('Setting up your business');
          await ensureEntityForEnv(env, token, partnerName, entityBody);
          if (env === GATE_ENV || primaryPartnerName === undefined) primaryPartnerName = partnerName;
        } catch (envError) {
          if (env !== 'production') throw envError;
          console.error('[onboarding] production provisioning failed — skipping (temporary):', envError);
          skippedEnvs.push(env);
        }
      }
      if (!primaryPartnerName) throw new Error('No environment is configured for provisioning.');

      trackEvent('onboarding', 'Business Created', {
        buildingFor: v.buildingFor,
        businessType: v.businessType,
        taxIdType: v.taxIdType,
        environmentCount: ONBOARDING_ENVS.length - skippedEnvs.length,
        ...(skippedEnvs.length ? { skippedEnvs: skippedEnvs.join(',') } : {}),
      });
      app.signIn({ partnerName: primaryPartnerName, companyName: v.legalName, email, partner: 'self-serve' });
      // Suppress the already-onboarded bounce before flipping onboarded: this user just finished the wizard
      // and must land on the 'done' screen, not be yanked to /dashboard by the reactive bounce effect.
      bouncedRef.current = true;
      app.markOnboarded();
      goStep('done');
    } catch (ex: any) {
      // `reason` carries the failure code (e.g. APPLICATION_NOT_APPROVED from vetting, or a provisioning /
      // KYB error) — never a form value, so no PII leaks. `v` is block-scoped to the try, so read the
      // business type fresh here.
      trackEvent('onboarding', 'Business Creation Failed', { businessType: getValues().businessType, reason: ex?.message });
      setSubmitErr(ex?.message || 'We could not finish setting up your account. Please try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      setSubmitPhase('');
    }
  };

  const milestones = [
    { label: 'How you’ll use the API', sub: 'Your use case', steps: ['usecase'] },
    { label: 'About you', sub: 'Point of contact', steps: ['you'] },
    { label: 'Business owner', sub: 'Who owns the business', steps: ['owner'] },
    { label: 'Business profile', sub: 'Name, industry & contact', steps: ['business'] },
    { label: 'Business type & tax', sub: 'Formation & tax ID', steps: ['legal'] },
    { label: 'Addresses', sub: 'Legal & operating', steps: ['address'] },
    { label: 'Review & create', sub: 'Confirm and submit', steps: ['review'] },
  ];
  const activeMilestone = step === 'done' ? milestones.length : milestones.findIndex((m) => m.steps.includes(step));

  // Enter key: advance the current step without clicking Continue.
  const makeKeyDown = (advanceFn: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    if (tag === 'BUTTON' || tag === 'A' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (target.closest('[role="listbox"]') || target.closest('[role="option"]') || target.closest('[role="combobox"]')) return;
    e.preventDefault();
    advanceFn();
  };

  const v = getValues();

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div
        style={{
          height: 64,
          borderBottom: '1px solid var(--border-static)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 28px',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => go('/')}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <MelioLogo height={22} />
          <span style={{ width: 1, height: 18, background: 'var(--border-static)' }} />
          <Text textStyle="body3Semi">API</Text>
        </button>
        <div style={{ flex: 1 }} />
      </div>

      <div className="grid-2" style={{ flex: 1, gridTemplateColumns: '0.85fr 1.15fr', gap: 0, alignItems: 'stretch' }}>
        {/* Left brand + milestone panel */}
        <div
          style={{
            background: 'linear-gradient(155deg, #1c1428, #16171f 70%)',
            padding: '56px 48px',
            color: 'var(--text-inverse)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: -80,
              bottom: -80,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.35), transparent 70%)',
            }}
          />
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <Container paddingBottom="s">
              <Text as="h1" textStyle="display2Semi" color="semantic.text.inverse">
                Start moving money in minutes
              </Text>
            </Container>
            <Container paddingBottom="xxl">
              <Text textStyle="body2" color="global.neutral.700">
                Tell us a little about you and your business, and we&apos;ll create your partner and working API keys. You&apos;ll land in
                test mode.
              </Text>
            </Container>
            <div
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14,
                padding: 24,
              }}
            >
              {/* Kept as hand-rolled markup rather than penny's <Tracker>: this rail sits on the dark
                  brand panel, and penny's Tracker is themed for light surfaces — its vertical variant
                  draws a light halo behind each indicator and gives the titles dark semantic colors,
                  neither of which is overridable. It also sizes each step to its content, which
                  overflowed this fixed-height panel. Mirrors welcome.tsx's <MilestoneRail>. */}
              {milestones.map((s, i) => {
                const done = activeMilestone > i,
                  active = activeMilestone === i;
                return (
                  <div key={i} style={{ display: 'flex', gap: 14, opacity: done || active ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: done ? '#8fd6a8' : active ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                          border: active ? '2px solid var(--accent)' : 'none',
                        }}
                      >
                        {done ? (
                          <Icon name="checked" size={14} color="#16171f" />
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{i + 1}</span>
                        )}
                      </div>
                      {i < milestones.length - 1 && (
                        <div
                          style={{
                            width: 2,
                            flex: 1,
                            minHeight: 22,
                            background: done ? '#8fd6a8' : 'rgba(255,255,255,0.12)',
                            margin: '3px 0',
                          }}
                        />
                      )}
                    </div>
                    <div style={{ paddingBottom: 18 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{s.label}</div>
                      <div style={{ fontSize: 12, color: '#9aa3b2', marginTop: 1 }}>{s.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ position: 'fixed', left: 20, bottom: 20, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {app.email && (
              <Group variant="horizontal" spacing="xxs" alignItems="center">
                <Icon name="user" size={14} color="#9aa3b2" />
                <Text
                  textStyle="caption1"
                  color="global.neutral.700"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}
                >
                  {app.email}
                </Text>
              </Group>
            )}
            {/* penny-backed: a secondary-inverse <Button>, which is penny's variant for a control
                sitting on a dark surface. Replaces a hand-rolled button that swapped its own
                colors through onMouseEnter/onMouseLeave handlers. */}
            <div style={{ alignSelf: 'flex-start' }}>
              <PennyButton
                size="small"
                variant="secondary-inverse"
                label="Log out"
                leftElement={<Icon name="exit" size={16} />}
                onClick={() => {
                  trackEvent('onboarding', 'Logged Out', { step });
                  app.signOut();
                }}
              />
            </div>
          </div>
        </div>

        {/* Right form panel — a column so the legal notice can sit at its foot (marginTop:auto)
            on short steps and simply follow the form on long ones. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '56px 48px',
            overflowY: 'auto',
          }}
        >
          <div style={{ width: '100%', maxWidth: 460 }}>
            {/* ── Step: usecase (beta application) ── */}
            {step === 'usecase' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(goToYou)}>
                <StepHeader title={<>How will you use the API?</>}>
                  Tell us what you're building. We review every application to keep the Melio API secure.
                </StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('buildingFor')}
                    labelProps={{ label: 'Which describes your integration? *' }}
                    render={(props: any) => (
                      <SelectNew
                        {...props}
                        options={buildingForOptions}
                        optionRenderer={(option: any) => (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Text textStyle="body3Semi">{option.label}</Text>
                            {option.description && (
                              <Text textStyle="body4" color="global.neutral.900">
                                {option.description}
                              </Text>
                            )}
                          </div>
                        )}
                        placeholder="Select an option"
                      />
                    )}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('projectDescription')}
                    labelProps={{ label: 'What are you building? *' }}
                    helperText="Describe your project and how it will use Melio's Payments API."
                    render={(props: any) => (
                      <TextArea
                        {...props}
                        numberOfRows={4}
                        placeholder="e.g. A vertical SaaS that pays out marketplace sellers on our platform."
                      />
                    )}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToYou}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: you (point of contact) ── */}
            {step === 'you' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(goToOwner)}>
                <StepHeader title={<>Tell us about you</>}>
                  You're the point of contact for this account. These details help us verify your identity and keep your account secure.
                </StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    isRequired
                    {...registerField('contactFirst')}
                    labelProps={{ label: 'First name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Dana" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('contactLast')}
                    labelProps={{ label: 'Last name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Reyes" />}
                  />
                  <Form.DateField
                    {...registerField('contactDob')}
                    colSpan={2}
                    isRequired
                    labelProps={{ label: 'Date of birth' }}
                    isTypable
                    format="MM/dd/yyyy"
                    placeholder="MM/DD/YYYY"
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('contactPhone')}
                    labelProps={{ label: 'Personal phone number *' }}
                    helperText="For account security — a mobile number works best."
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('usecase')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToOwner}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: owner (business owner) ── */}
            {step === 'owner' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(goToBusiness)}>
                <StepHeader title={<>Business owner</>}>
                  The primary individual who owns or controls the business — we're required to verify them. We've prefilled your details;
                  edit them if the owner is someone else.
                </StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    isRequired
                    {...registerField('ownerFirst')}
                    labelProps={{ label: 'First name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Jane" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('ownerLast')}
                    labelProps={{ label: 'Last name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Doe" />}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('ownerEmail')}
                    labelProps={{ label: 'Email *' }}
                    render={(props: any) => <TextField {...props} type="email" placeholder="jane.doe@sunrisebakery.co" />}
                  />
                  <Form.DateField
                    {...registerField('ownerDob')}
                    colSpan={2}
                    isRequired
                    labelProps={{ label: 'Date of birth' }}
                    isTypable
                    format="MM/dd/yyyy"
                    placeholder="MM/DD/YYYY"
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('ownerPhone')}
                    labelProps={{ label: 'Owner phone number *' }}
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('you')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToBusiness}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: business (profile) ── */}
            {step === 'business' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(goToLegal)}>
                <StepHeader title={<>Tell us about your business</>}>
                  Provide the name of your business and the industry it operates in.
                </StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('legalName')}
                    labelProps={{ label: 'Business name *' }}
                    helperText="How your business appears in Melio"
                    render={(props: any) => <TextField {...props} placeholder="Sunrise Bakery LLC" />}
                  />
                  <IndustryField
                    form={form}
                    env={app.env}
                    name="industry"
                    label="Business industry"
                    isRequired
                    colSpan={2}
                    getToken={app.getAccessToken}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('phone')}
                    labelProps={{ label: 'Business phone number *' }}
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('website')}
                    labelProps={{ label: 'Business website *' }}
                    helperText="Shown to your customers in emails from us."
                    render={(props: any) => <TextField {...props} placeholder="https://sunrisebakery.co" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('owner')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToLegal}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: legal (business type & tax) ── */}
            {step === 'legal' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(goToAddress)}>
                <StepHeader title={<>Legal information</>}>The legal entity money is paid from — its formation type and tax ID.</StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('businessType')}
                    labelProps={{ label: 'Business type *' }}
                    render={(props: any) => (
                      <SelectNew
                        {...props}
                        options={businessTypeOptions}
                        optionRenderer={(option: any) => (
                          <div>
                            <Text textStyle="body3Semi">{option.label}</Text>
                            {option.description && (
                              <Text textStyle="body4" color="global.neutral.900">
                                {option.description}
                              </Text>
                            )}
                          </div>
                        )}
                        placeholder="Select a type"
                      />
                    )}
                  />
                  <FormField
                    isRequired
                    {...registerField('taxIdType')}
                    labelProps={{ label: 'Tax ID type *' }}
                    render={(props: any) => <SelectNew {...props} options={taxTypeOptions} placeholder="Select" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('taxId')}
                    labelProps={{ label: `${taxIdType === 'ein' ? 'EIN' : String(taxIdType || '').toUpperCase()} *` }}
                    render={(props: any) => <TextField {...props} placeholder={taxIdType === 'ein' ? '84-3920481' : '123-45-6789'} />}
                  />
                  <FormField
                    colSpan={2}
                    {...registerField('description')}
                    labelProps={{ label: 'Business description' }}
                    render={(props: any) => <TextField {...props} placeholder="What your business does (optional)" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('business')}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToAddress}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: address (legal + operating, inner sub-step) ── */}
            {step === 'address' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(addressSub === 0 ? goToOperating : goToReview)}>
                {/* Sub 0: legal registration address */}
                {addressSub === 0 && (
                  <>
                    <StepHeader title={<>Legal registration address</>}>
                      The address the business is registered at. Start typing to autocomplete.
                    </StepHeader>
                    <Form {...formProps} columns={2}>
                      <AddressFields
                        form={form}
                        env={app.env}
                        parentFieldName="legalAddress"
                        label="Legal address *"
                        getToken={app.getAccessToken}
                      />
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goStep('legal')}>
                        Back
                      </Button>
                      <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToOperating}>
                        Continue
                      </Button>
                    </div>
                  </>
                )}

                {/* Sub 1: operating address */}
                {addressSub === 1 && (
                  <>
                    <StepHeader title={<>Operating address</>}>
                      Where the business operates day to day. Start typing to autocomplete.
                    </StepHeader>
                    <Form {...formProps} columns={2}>
                      <FormField
                        colSpan={2}
                        labelProps={{ label: 'Operating address', isHidden: true }}
                        render={() => (
                          <Checkbox
                            value={operatingSameAsLegal}
                            onChange={(checked: boolean) => setValue('operatingSameAsLegal', checked)}
                            label="Same as legal registration address"
                          />
                        )}
                      />
                      {!operatingSameAsLegal && (
                        <AddressFields
                          form={form}
                          env={app.env}
                          parentFieldName="address"
                          label="Operating address *"
                          getToken={app.getAccessToken}
                        />
                      )}
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={() => goAddressSub(0)}>
                        Back
                      </Button>
                      <Button fullWidth size="large" rightIcon="arrow-right" onClick={goToReview}>
                        Continue
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Step: review & create ── */}
            {step === 'review' && (
              <div style={{ animation: 'om-fade-up .3s ease' }}>
                <StepHeader title={<>Review & create</>}>One last look before you submit.</StepHeader>
                <div style={{ border: '1px solid var(--border-static)', borderRadius: 12, padding: '4px 18px 14px' }}>
                  <SummaryRow label="Business name" value={v.legalName} />
                  <SummaryRow
                    label="Business type"
                    value={businessTypeOptions.find((o) => o.value === v.businessType)?.label || v.businessType}
                  />
                  <SummaryRow label="Industry" value={v.industry?.name || v.industry?.naicsCode} />
                  <SummaryRow label="Business phone" value={normPhone(v.phone)} />
                  {v.website ? <SummaryRow label="Website" value={v.website} /> : null}
                  <SummaryRow
                    label="Tax ID"
                    value={`${taxTypeOptions.find((o) => o.value === v.taxIdType)?.label || v.taxIdType} · ${v.taxId}`}
                  />
                  <SummaryRow
                    label="Legal address"
                    value={[v.legalAddress.line1, v.legalAddress.city, v.legalAddress.state, v.legalAddress.postalCode]
                      .filter(Boolean)
                      .join(', ')}
                  />
                  <SummaryRow
                    label="Operating address"
                    value={
                      v.operatingSameAsLegal
                        ? 'Same as legal'
                        : [v.address.line1, v.address.city, v.address.state, v.address.postalCode].filter(Boolean).join(', ')
                    }
                  />
                  <SummaryRow label="Owner" value={`${v.ownerFirst} ${v.ownerLast} · ${v.ownerEmail}`} />
                  <SummaryRow label="Point of contact" value={`${v.contactFirst} ${v.contactLast}`} />
                  <SummaryRow label="Use case" value={buildingForOptions.find((o) => o.value === v.buildingFor)?.label || v.buildingFor} />
                </div>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button
                    variant="tertiary"
                    size="large"
                    leftIcon="arrow-left"
                    disabled={submitting}
                    onClick={() => {
                      setStep('address');
                      goAddressSub(1);
                    }}
                  >
                    Back
                  </Button>
                  <Button fullWidth size="large" loading={submitting} onClick={createPartner}>
                    Submit
                  </Button>
                </div>
                {submitting && submitPhase && (
                  <Container paddingTop="s">
                    {/* role=status so the phase changes are announced, not just drawn. */}
                    <div role="status" aria-live="polite">
                      <Text textStyle="body4" color="semantic.text.secondary">
                        {submitPhase}
                      </Text>
                    </div>
                  </Container>
                )}
              </div>
            )}

            {/* ── Step: done ── */}
            {step === 'done' && (
              <div style={{ animation: 'om-fade-up .3s ease', textAlign: 'center' }}>
                <Illustration name="celebration" size={130} style={{ margin: '0 auto 8px' }} />
                <Text as="h2" textStyle="heading1Semi">
                  You&apos;re all set{watch('legalName') ? `, ${watch('legalName')}` : ''}
                </Text>
                <Container paddingTop="xs" paddingBottom="xs">
                  <Text textStyle="body2" color="semantic.text.secondary">
                    Thanks for moving forward in the Melio API Beta Program.
                  </Text>
                </Container>
                <Container paddingBottom="m">
                  <Text textStyle="body2" color="semantic.text.secondary">
                    While we review your details, start building in the staging environment. Explore the API and test your integration in
                    the simulator. We&apos;ll follow up if we have questions, and let you know as soon as you&apos;re set for production.
                  </Text>
                </Container>
                {/* Actions by priority: primary = continue into the app, secondary = talk to us,
                    link = optional reading (docs). Simulator lives inside the dashboard, so it's not
                    offered here as a pre-dashboard step. */}
                <Button
                  fullWidth
                  size="large"
                  rightIcon="arrow-right"
                  onClick={() => {
                    trackEvent('onboarding', 'Dashboard Opened', {});
                    app.markOnboarded();
                    go('/dashboard');
                  }}
                >
                  Open dashboard
                </Button>
                <Button
                  fullWidth
                  variant="secondary"
                  size="large"
                  style={{ marginTop: 12 }}
                  onClick={() => window.open(SCHEDULE_URL, '_blank', 'noopener,noreferrer')}
                >
                  Schedule a call
                </Button>
                <Button
                  fullWidth
                  variant="ghost"
                  size="large"
                  style={{ marginTop: 4 }}
                  onClick={() => window.open('/reference', '_blank', 'noopener,noreferrer')}
                >
                  View the docs
                </Button>
              </div>
            )}
          </div>
          <div style={{ width: '100%', maxWidth: 460, marginTop: 'auto', paddingTop: 40 }}>
            <LegalNotice />
          </div>
        </div>
      </div>
    </div>
  );
}

export { Onboarding, KeyReveal, ApiKeyPanel };
